import { memo } from "react";
import type { ReactNode, Ref } from "react";
import Table from '../base_table';
import type { TableRef } from '../base_table';
import type {
    ColumnDef,
    SortingState,
    ColumnFiltersState,
    ColumnOrderState,
    RowSelectionState,
    PaginationState,
    ExpandedState,
} from "@tanstack/react-table";
import type { FilingPack } from "../../shared/Types";
import { TableSpinner, TableMessage, TableError } from '../base_table/table-components/TableStates';

export interface FilingPacksTableProps {
    tableRef: Ref<TableRef>;
    columns: ColumnDef<FilingPack, any>[];
    data: FilingPack[];
    isLoading: boolean;
    error: any;
    tableTitle?: ReactNode;
    renderExpandedContent?: (filingPack: FilingPack) => ReactNode;

    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    columnOrder: ColumnOrderState;
    rowSelection: RowSelectionState;
    expanded: ExpandedState;
    globalSearch?: string;
    columnFiltersSidePanelIsOpen: boolean;
    pageIndex: number;
    pageSize: number;
    totalCount?: number;

    onSortChange: (sorting: SortingState) => void;
    onColumnFiltersChange: (filters: ColumnFiltersState) => void;
    onColumnOrderChange: (order: ColumnOrderState) => void;
    onRowSelectionChange: (selection: RowSelectionState) => void;
    onPaginationChange: (pagination: PaginationState) => void;
    onGlobalFilterChange: (filter: string) => void;
    onExpandedChange: (expanded: ExpandedState) => void;
    onColumnFiltersSidePanelOpenChange: (isOpen: boolean) => void;
}

export const FilingPacksTable = memo(
    function FilingPacksTable({
        tableRef,
        columns,
        data,
        isLoading,
        error,
        tableTitle,
        renderExpandedContent,
        sorting,
        columnFilters,
        columnOrder,
        rowSelection,
        expanded,
        globalSearch,
        columnFiltersSidePanelIsOpen,
        pageIndex,
        pageSize,
        totalCount,
        onSortChange,
        onColumnFiltersChange,
        onColumnOrderChange,
        onRowSelectionChange,
        onPaginationChange,
        onGlobalFilterChange,
        onExpandedChange,
        onColumnFiltersSidePanelOpenChange,
    }: FilingPacksTableProps) {
        if (error) return (
            <TableError message={error.message || String(error)} />
        );

        return (
            <div style={{ height: "100%" }}>
                <Table
                    ref={tableRef}
                    className="filing-packs-table"
                    columns={columns as any}
                    data={data}
                    tableTitle={tableTitle}
                    enableSorting
                    manualSorting
                    enablePagination
                    manualPagination
                    manualFiltering
                    enableRowSelection
                    enableMultiRowSelection={false}
                    enableGlobalFilter
                    enableColumnFiltersSidePanel
                    enableColumnFilters={false}
                    getRowId={(row: FilingPack) => row.id ?? ""}
                    initialGlobalFilter={globalSearch}
                    globalFilterPlaceholder="Search by LRN, MRN..."
                    renderExpandedContent={renderExpandedContent}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onSortChange={onSortChange}
                    onColumnFiltersChange={onColumnFiltersChange}
                    onColumnOrderChange={onColumnOrderChange}
                    onRowSelectionChange={onRowSelectionChange}
                    onPaginationChange={onPaginationChange}
                    onGlobalFilterChange={onGlobalFilterChange}
                    onExpandedChange={onExpandedChange}
                    onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
                    noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Filing Packs Found" />}
                    noDataText={isLoading ? "Loading filing packs..." : "No filing packs found."}
                    state={{
                        sorting,
                        columnFilters,
                        columnOrder,
                        rowSelection,
                        expanded,
                        columnFiltersSidePanelIsOpen,
                        pagination: { pageIndex, pageSize },
                    }}
                />
            </div>
        );
    }
);
